import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { Event, ApiResponse, PaginatedResponse, User, ChatMessage, ReminderForm } from '../types';
import { toast } from 'sonner';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000, // Increased timeout to 30 seconds for remote database
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto-refresh token logic
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// Response interceptor with auto-refresh
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.code === 'ECONNABORTED') {
      toast.error('Tiempo de espera agotado. Verifica tu conexión.');
      return Promise.reject(error);
    }

    // Auto-refresh on 401 (skip if already retrying or if it's the refresh endpoint itself)
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        toast.error('Sesión expirada. Inicia sesión nuevamente.');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${api.defaults.baseURL}/auth/refresh`, { refreshToken });
        const newToken = response.data?.data?.token || response.data?.token;
        if (newToken) {
          localStorage.setItem('auth_token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return api(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        toast.error('Sesión expirada. Inicia sesión nuevamente.');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      toast.error('No tienes permisos para realizar esta acción.');
    } else if (error.response?.status && error.response.status >= 500) {
      toast.error('Error del servidor. Intenta nuevamente más tarde.');
    } else if (!navigator.onLine) {
      toast.error('Sin conexión a internet. Modo offline activado.');
    }

    return Promise.reject(error);
  }
);

/**
 * Authenticated fetch wrapper with auto-refresh.
 * Use this instead of raw fetch() in components like AdminPage.
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        const refreshRes = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          const newToken = data?.data?.token || data?.token;
          if (newToken) {
            localStorage.setItem('auth_token', newToken);
            headers['Authorization'] = `Bearer ${newToken}`;
            response = await fetch(url, { ...options, headers });
          }
        }
      } catch {
        // refresh failed, redirect to login
      }
    }
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  }

  return response;
};

// Event API
export const eventAPI = {
  // Get all events
  getEvents: async (params?: {
    date?: string;
    category?: string;
    status?: string;
    startTime?: string;
    endTime?: string;
    page?: number;
    limit?: number;
  }): Promise<Event[]> => {
    // Set default limit to 100 if not specified
    const requestParams = {
      ...params,
      limit: params?.limit || 100
    };
    const response = await api.get<ApiResponse<any[]>>('/events', { params: requestParams });
    // Map Category.name to category field for backward compatibility
    return response.data.data.map((event: any) => ({
      ...event,
      category: event.EventCategories?.[0]?.Category?.name || event.category || event.legacyCategory || 'OTHER',
    }));
  },

  // Get single event
  getEvent: async (id: string): Promise<Event> => {
    const response = await api.get<ApiResponse<any>>(`/events/${id}`);
    const event = response.data.data;
    return {
      ...event,
      category: event.EventCategories?.[0]?.Category?.name || event.category || event.legacyCategory || 'OTHER',
    };
  },

  // Get events by date range
  getEventsByDateRange: async (startTime: string, endTime: string): Promise<Event[]> => {
    const response = await api.get<ApiResponse<Event[]>>('/events/range', {
      params: { startTime, endTime }
    });
    return response.data.data;
  },

  // Create event (admin only)
  createEvent: async (eventData: Omit<Event, 'id'>): Promise<Event> => {
    const response = await api.post<ApiResponse<Event>>('/events', eventData);
    return response.data.data;
  },

  // Update event (admin only)
  updateEvent: async (id: string, eventData: Partial<Event>): Promise<Event> => {
    const response = await api.put<ApiResponse<Event>>(`/events/${id}`, eventData);
    return response.data.data;
  },

  // Delete event (admin only)
  deleteEvent: async (id: string): Promise<void> => {
    await api.delete(`/events/${id}`);
  },

  // Get live events (streaming)
  getLiveEvents: async (): Promise<Event[]> => {
    const response = await api.get<ApiResponse<any[]>>('/events/live');
    return response.data.data.map((event: any) => ({
      ...event,
      category: event.EventCategories?.[0]?.Category?.name || event.category || event.legacyCategory || 'OTHER',
    }));
  },
};

// Reminder API
export const reminderAPI = {
  // Get user reminders
  getReminders: async (): Promise<string[]> => {
    const response = await api.get<ApiResponse<string[]>>('/reminders');
    return response.data.data;
  },

  // Set reminder
  setReminder: async (data: ReminderForm): Promise<void> => {
    await api.post('/reminders', {
      eventId: data.eventId,
      notifyBefore: data.minutesBefore, // Map minutesBefore to notifyBefore
    });
  },

  // Remove reminder
  removeReminder: async (eventId: string): Promise<void> => {
    await api.delete(`/reminders/${eventId}`);
  },

  // Test notification
  testNotification: async (): Promise<void> => {
    await api.post('/reminders/test');
  },
};

// Chat API (Acty)
export const chatAPI = {
  // Send message to Acty
  sendMessage: async (message: string): Promise<ChatMessage> => {
    const response = await api.post<ApiResponse<ChatMessage>>('/chat', {
      message,
      timestamp: new Date().toISOString(),
    });
    return response.data.data;
  },

  // Get chat history
  getChatHistory: async (): Promise<ChatMessage[]> => {
    const response = await api.get<ApiResponse<ChatMessage[]>>('/chat/history');
    return response.data.data;
  },

  // Clear chat history
  clearChatHistory: async (): Promise<void> => {
    await api.delete('/chat/history');
  },
};

// User API
export const userAPI = {
  // Get current user profile
  getProfile: async (): Promise<User> => {
    const response = await api.get<ApiResponse<User>>('/user/profile');
    return response.data.data;
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    const response = await api.put<ApiResponse<User>>('/user/profile', userData);
    return response.data.data;
  },

  // Update user preferences
  updatePreferences: async (preferences: User['preferences']): Promise<User> => {
    const response = await api.put<ApiResponse<User>>('/user/preferences', preferences);
    return response.data.data;
  },
};

// Auth API
export const authAPI = {
  // Login
  login: async (email: string, password: string): Promise<{ user: User; token: string }> => {
    const response = await api.post<ApiResponse<{ user: User; token: string }>>('/auth/login', {
      email,
      password,
    });
    return response.data.data;
  },

  // Register
  register: async (userData: {
    name: string;
    email: string;
    password: string;
  }): Promise<{ user: User; token: string }> => {
    const response = await api.post<ApiResponse<{ user: User; token: string }>>('/auth/register', userData);
    return response.data.data;
  },

  // Logout
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    localStorage.removeItem('auth_token');
  },

  // Refresh token
  refreshToken: async (): Promise<{ token: string }> => {
    const response = await api.post<ApiResponse<{ token: string }>>('/auth/refresh');
    return response.data.data;
  },
};

// Analytics API
export const analyticsAPI = {
  // Track event interaction
  trackEventInteraction: async (eventId: string, action: string): Promise<void> => {
    await api.post('/analytics/event-interaction', {
      eventType: `event_${action}`, // Format: event_view, event_remind, event_share
      eventData: {
        eventId,
        action,
        timestamp: new Date().toISOString(),
      },
    });
  },

  // Get event analytics (admin only)
  getEventAnalytics: async (eventId: string): Promise<any> => {
    const response = await api.get(`/analytics/events/${eventId}`);
    return response.data.data;
  },
};

// Push notification API
export const notificationAPI = {
  // Subscribe to push notifications
  subscribe: async (subscription: PushSubscription): Promise<void> => {
    await api.post('/notifications/subscribe', {
      subscription: subscription.toJSON(),
    });
  },

  // Unsubscribe from push notifications
  unsubscribe: async (): Promise<void> => {
    await api.post('/notifications/unsubscribe');
  },

  // Send test notification
  sendTestNotification: async (): Promise<void> => {
    await api.post('/notifications/test');
  },
};

// Health check API
export const healthAPI = {
  // Check API health
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Settings API
export const settingsAPI = {
  // Get public settings
  getPublicSettings: async (): Promise<Record<string, any>> => {
    const response = await api.get<ApiResponse<Record<string, any>>>('/settings/public');
    return response.data.data;
  },

  // Get all settings (admin only)
  getSettings: async (category?: string): Promise<Record<string, any>> => {
    const response = await api.get<ApiResponse<Record<string, any>>>('/settings', {
      params: { category },
    });
    return response.data.data;
  },

  // Update settings (admin only)
  updateSettings: async (settings: Record<string, any>): Promise<void> => {
    await api.put('/settings', settings);
  },

  // Initialize default settings (admin only)
  initializeSettings: async (): Promise<void> => {
    await api.post('/settings/init');
  },
};

// Utility functions
export const apiUtils = {
  // Check if user is online
  isOnline: (): boolean => navigator.onLine,

  // Retry failed requests
  retry: async <T>(fn: () => Promise<T>, retries = 3): Promise<T> => {
    try {
      return await fn();
    } catch (error) {
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return apiUtils.retry(fn, retries - 1);
      }
      throw error;
    }
  },

  // Cache API response
  cache: new Map<string, { data: any; timestamp: number; ttl: number }>(),

  // Get cached data
  getCached: <T>(key: string): T | null => {
    const cached = apiUtils.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    apiUtils.cache.delete(key);
    return null;
  },

  // Set cached data
  setCached: <T>(key: string, data: T, ttl = 5 * 60 * 1000): void => {
    apiUtils.cache.set(key, { data, timestamp: Date.now(), ttl });
  },
};

export default api;